import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BulkPlateOrder } from '../../inventory/models/views/bulk-plate-order.model';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { CustomerPlateModel } from '../models/customer-plate.model';
import { DisabilityCertificationModel } from '../models/disability-certification.model';
import { OwnershipCustomerRelationshipModel } from '../models/ownership-customer-relationship.model';
import { PlateTypeProductMapModel } from '../models/plate-type-product-map.model';
import { PlateModel } from '../models/plate.model';

@Injectable()
export class PlateService extends BaseApiService {

    getTransferPlateInformation = (plateNumber: string) => this.get('TitleRegistration/Plate/TransferPlateInformation/' + plateNumber);

    getAssignPlateInformation = (plateNumber: string) => this.get('TitleRegistration/Plate/AssignPlateInformation/' + plateNumber);

    getPlatesAvailableForTransfer = (ownershipCustomerRelationships: OwnershipCustomerRelationshipModel[]) => {
        var url = 'TitleRegistration/Plate/PlatesAvailableForTransfer';
        for (const ownershipCustomerRelationship of ownershipCustomerRelationships) {
            if (ownershipCustomerRelationship.customerNumberID == ownershipCustomerRelationships[0].customerNumberID) {
                url = url + '?customerNumbers=' + ownershipCustomerRelationship.customerNumberID;
            } else {
                url = url + '&customerNumbers=' + ownershipCustomerRelationship.customerNumberID;
            }
        }

        return this.get(url);
    };

    getPlateLookups = () =>
        // ToDo:  Return dictionaryitem[];
        this.get('TitleRegistration/Plate/GetPlateLookups');

    getPlateTypes = () =>
        // ToDo:  Return SelectListItemModel[];
        this.get('TitleRegistration/Plate/GetPlateTypes');


    /**
     * Gets the plate types that are available based on the vehicle type and registration use type.
     * @param vehicleId: Vehicle Type ID.
     * @param registrationUseTypeId: Registration Use Type ID.
     * @returns: List of available plate types.
     */
    getPlateTypesByVehicleTypeRegistrationUseType = (vehicleId: number, registrationUseTypeId: number) => this.get('TitleRegistration/Plate/Types/VehicleType/' + vehicleId + '/RegistrationUseType/' + registrationUseTypeId);

    getPlateTypesByVehicleTypeRegistrationUseTypeIsPersonalized = (vehicleId: number, registrationUseTypeId: number, isPersonalized: boolean) => this.get('TitleRegistration/Plate/Types/VehicleType/' + vehicleId + '/RegistrationUseType/' + registrationUseTypeId + '/IsPersonalized/' + isPersonalized);

    getVehicleOwnershipPlateByAction = (plateAction: string, vehicleOwnershipPlateId: number) => this.get('TitleRegistration/Plate/VehicleOwnershipPlate/' + vehicleOwnershipPlateId + '/' + plateAction);

    manualUnReserve = (model: PlateModel): Observable<PlateModel> => this.post('TitleRegistration/Plate/ManualUnReserve/', model);
    /**
     * Consumes plate with from inventory
     * @param model: Plate to consume out of inventory.
     * @param businessTransactionId: Business transaction Id.
     * @returns: Plate model if request to inventory was successful.
     */
    consumePlate = (model: PlateModel, businessTransactionId: number): Observable<PlateModel> => this.post('TitleRegistration/Plate/Consume/Trn/' + businessTransactionId, model);

    assignPlate = (model: PlateModel, businessTransactionId: number): Observable<PlateModel> => this.post('TitleRegistration/Plate/Assign/Trn/' + businessTransactionId, model);

    updateTransferPlate = (model: PlateModel, businessTransactionId: number): Observable<PlateModel> => this.post('TitleRegistration/Plate/UpdateTransferPlate/Trn/' + businessTransactionId, model);

    allocatePlates = (model: BulkPlateOrder): Observable<BulkPlateOrder> => this.post('TitleRegistration/Plate/Allocate/', model);

    /**
     * Releases the reservation on the plate in inventory.
     * @param model: Plate to release back to inventory.
     * @param businessTransactionId: Business transaction ID.
     * @returns: Plate model if release to inventory was successful.
     */
    releasePlate = (model: PlateModel, businessTransactionId: number): Observable<PlateModel> => this.post('TitleRegistration/Plate/Release/Trn/' + businessTransactionId, model);

    /**
     * Assigns the placard by pulling the requested placard from inventory.
     * @param model: Placard to assign.
     * @param businessTransactionId: Business transaction ID.
     * @returns: Any errors that occurred during the assignment.
     */
    assignPlacard = (model: CustomerPlateModel, businessTransactionId: number) => this.post('TitleRegistration/Placard/Temporary/Assign/Trn/' + businessTransactionId, model);

    /**
     * Updates placard notes to Wip.
     * @param model: Placard notes to update.
     * @param businessTransactionId: Business transaction ID.
     * @returns: Any errors that occurred during the assignment.
     */
    updatePlacardNotes = (model: CustomerPlateModel, businessTransactionId: number) => this.post('TitleRegistration/Placard/Trn/' + businessTransactionId + '/Notes', model);

    /**
     * Releases the placard reservation in inventory.
     * @param businessTransactionId: Business Transaction ID.
     * @returns: Any errors from attempting to release the placard.
     */
    releasePlacard = (businessTransactionId: number) => this.post('TitleRegistration/Placard/Trn/' + businessTransactionId + '/Notes', null);

    /**
     * Gets all the placards that are currently assigned to a customer.
     * @param customerNumberId: Customer to search with.
     * @returns: List of any placards associated to the customer.
     */
    getPlacardsByCustomerNumber = (customerNumberId: number): Observable<CustomerPlateModel[]> => this.get('TitleRegistration/Placard/Customer/' + customerNumberId);

    /**
 * Gets all the placards that are currently assigned to a customer.
 * @param customerNumberId: Customer to search with.
 * @returns: List of any placards associated to the customer.
 */
    getAllPlacardsByCustomerNumber = (customerNumberId: number) => this.get('TitleRegistration/Placard/Customer/' + customerNumberId + '/All');

    /**
     * Gets the placard record by the placard number.
     * @param placardNumber: Number of the placard.
     * @returns: The placard record that matches the placard number.
     */
    getPlacardByNumber = (placardNumber: string) => this.get('TitleRegistration/Placard/Number/' + placardNumber);

    /**
     * Gets all placard record by the placard number.
     * @param placardNumber: Number of the placard.
     * @returns: The placard records that matches the placard number.
     */
    getAllPlacardsByNumber = (placardNumber: string) => this.get('TitleRegistration/Placard/Placards/Number/' + placardNumber);

    /**
     * Gets all PlateTypeProductMaps
     * @returns: Array of PlateTypeProductMaps.
     */
    getPlateTypeProductMaps = (): Observable<PlateTypeProductMapModel[]> => this.get('TitleRegistration/Plate/Types/ProductMaps');

    addPlacardCustomerCharacteristic = (disabilityCertification: DisabilityCertificationModel, customerNumberId: number, customerCharacteristicTypeId: number) => this.post('TitleRegistration/TitleRegistrationCustomerCharacteristic/Certification/Customer/' + customerNumberId + '/CharacteristicType/' + customerCharacteristicTypeId, disabilityCertification);

    updateDisabilityCertification = (disabilityCertification: DisabilityCertificationModel, businessTransactionId: number) => this.post('TitleRegistration/Placard/Certification/' + businessTransactionId, disabilityCertification);

    getPlateConfigurationByPlateTypeId = (plateTypeId: number) => this.get('TitleRegistration/Plate/PlateConfiguration/PlateType/' + plateTypeId);

    getDocumentCategoryTypesByCustomerCharacteristicTypeId = (customerCharacteristicTypeId: number) => this.get('TitleRegistration/Plate/DocumentCategoryTypeIds/CustomerCharacteristicType/' + customerCharacteristicTypeId);

    /**
     * Gets the plate record by the plate number.
     * @param plateNumber: >Number of the plate.
     * @returns: The plate record that matches the plate number.
     */
    getPlatesByNumber = (plateNumber: string) => this.get('TitleRegistration/Plate/' + plateNumber);

    /**
     * Gets the plate by the vehicle id.
     * @param vehicleId: The vehicle id.
     * @returns: the plate.
     */
    getPlateByVehicleId = (vehicleId: number) => this.get('TitleRegistration/Plate?vehicleId=' + vehicleId);

    /**
     * Gets the plate by  id.
     * @param plateId: The plate id.
     * @returns: the plate.
     */
    getPlateByPlateId = (plateId: number) => this.get('TitleRegistration/Plate/Id/' + plateId);

    getPlateSearchResultByPlateNumber = (plateNumber: string, isExactMatch: boolean) => this.get('TitleRegistration/Plate/PlateSearchResult/' + plateNumber + '/' + isExactMatch);
}
